<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import camelcaseKeys from 'camelcase-keys';
import { useAlert } from 'dashboard/composables';
import DealAPI from 'dashboard/api/deals';
import { useDealsStore } from 'dashboard/stores/deals';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['linked']);

const { t } = useI18n();
const store = useStore();
const dealsStore = useDealsStore();

const dialogRef = ref(null);
// display_id della conversazione (l'API dei deal risolve per display_id).
const conversationId = ref(null);
const contactId = ref(null);
const availableDeals = ref([]);
const selectedDealId = ref('');
const newDealName = ref('');
const isLoading = ref(false);
const isSubmitting = ref(false);

// Rinfresca la conversazione nello store così il badge del deal compare subito
// nella card/header, senza attendere un refresh manuale della lista.
const refreshConversation = () => {
  if (conversationId.value) {
    store.dispatch('getConversation', conversationId.value);
  }
};

const defaultPipeline = computed(
  () =>
    dealsStore.pipelines.find(pipeline => pipeline.isDefault) ||
    dealsStore.pipelines[0]
);

const dealOptions = computed(() =>
  availableDeals.value.map(deal => ({ value: deal.id, label: deal.name }))
);

const loadDeals = async () => {
  isLoading.value = true;
  try {
    if (!dealsStore.pipelines.length) await dealsStore.fetchPipelines();
    if (!defaultPipeline.value) {
      availableDeals.value = [];
      return;
    }
    // Deal gia' collegati a questa conversazione: da escludere dalla scelta.
    const {
      data: { payload: linkedPayload },
    } = await DealAPI.get({ conversation_id: conversationId.value });
    const linkedIds = new Set(
      camelcaseKeys(linkedPayload || [], { deep: true }).map(deal => deal.id)
    );
    const {
      data: { payload },
    } = await DealAPI.get({ pipeline_id: defaultPipeline.value.id });
    availableDeals.value = camelcaseKeys(payload || [], { deep: true }).filter(
      deal => !linkedIds.has(deal.id)
    );
  } catch (error) {
    availableDeals.value = [];
  } finally {
    isLoading.value = false;
  }
};

const open = (convId, ctId) => {
  conversationId.value = convId;
  contactId.value = ctId || null;
  selectedDealId.value = '';
  newDealName.value = '';
  availableDeals.value = [];
  dialogRef.value?.open();
  loadDeals();
};

const linkExisting = async () => {
  if (!selectedDealId.value || isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    await dealsStore.linkConversation(
      Number(selectedDealId.value),
      conversationId.value
    );
    refreshConversation();
    useAlert(t('DEALS.CONVERSATION_PANEL.LINK_SUCCESS'));
    emit('linked');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message || t('DEALS.CONVERSATION_PANEL.LINK_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};

const createAndLink = async () => {
  if (!newDealName.value.trim() || isSubmitting.value) return;
  if (!defaultPipeline.value) {
    useAlert(t('DEALS.CONVERSATION_PANEL.NO_PIPELINE'));
    return;
  }
  isSubmitting.value = true;
  try {
    await dealsStore.createDeal({
      name: newDealName.value.trim(),
      pipelineId: defaultPipeline.value.id,
      contactId: contactId.value,
      conversationId: conversationId.value,
    });
    refreshConversation();
    useAlert(t('DEALS.CREATE.API.SUCCESS_MESSAGE'));
    emit('linked');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error.message || t('DEALS.CREATE.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :title="t('DEALS.ADD_TO_DEAL.TITLE')"
    :show-confirm-button="false"
    :cancel-button-label="t('DIALOG.BUTTONS.CANCEL')"
  >
    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner :size="16" />
    </div>

    <div v-else class="flex flex-col gap-5">
      <!-- Collega a un deal esistente -->
      <div class="flex flex-col gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('DEALS.ADD_TO_DEAL.LINK_EXISTING') }}
        </span>
        <div class="flex items-center gap-2">
          <Select
            :model-value="selectedDealId"
            :options="dealOptions"
            :placeholder="
              dealOptions.length
                ? t('DEALS.ADD_TO_DEAL.PICK_DEAL')
                : t('DEALS.ADD_TO_DEAL.NO_DEALS')
            "
            :disabled="!dealOptions.length || isSubmitting"
            class="!w-full grow"
            @update:model-value="value => (selectedDealId = value)"
          />
          <Button
            :label="t('DEALS.ADD_TO_DEAL.LINK')"
            size="sm"
            :disabled="!selectedDealId || isSubmitting"
            @click="linkExisting"
          />
        </div>
      </div>

      <div class="flex items-center gap-2 text-xs text-n-slate-10">
        <span class="h-px grow bg-n-weak" />
        {{ t('DEALS.ADD_TO_DEAL.OR') }}
        <span class="h-px grow bg-n-weak" />
      </div>

      <!-- Crea un nuovo deal collegato a questa conversazione -->
      <div class="flex flex-col gap-2">
        <span class="text-xs font-medium text-n-slate-11">
          {{ t('DEALS.ADD_TO_DEAL.CREATE_NEW') }}
        </span>
        <div class="flex items-center gap-2">
          <Input
            v-model="newDealName"
            :placeholder="t('DEALS.CREATE.FIELDS.NAME')"
            :disabled="isSubmitting"
            class="w-full grow"
            @keydown.enter.prevent="createAndLink"
          />
          <Button
            :label="t('DEALS.ADD_TO_DEAL.CREATE')"
            variant="outline"
            size="sm"
            :disabled="!newDealName.trim() || isSubmitting"
            @click="createAndLink"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>
